import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import {
  getTaskDetailsApi,
  getTaskResponseApi,
} from "../api/TaskActions";

interface FormField {
  field_id: number;
  field_label: string;
  field_type: string;
  field_order?: number;
  is_required?: boolean | number;
}

interface FormDetails {
  task_id: number;
  task_title: string;
  task_description?: string | null;
  deadline?: string | null;
  allowed_responses?: number | null;
  audience_type?: string | null;
  status?: string | null;
  created_at?: string | null;
  form_fields?: FormField[];
}

interface SubmittedAnswer {
  field_id: number;
  field_label: string;
  field_type: string;
  answer:
    | string
    | number
    | string[]
    | null;
}

interface SubmittedResponse {
  response_id: number;
  status: string;
  submitted_at: string;
  answers: SubmittedAnswer[];
}

const formatDateTime = (
  value?: string | null,
) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (
  value?: string | number | null,
) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  const date = new Date(String(value));

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isFileType = (
  fieldType?: string,
) =>
  ["file", "image"].includes(
    String(fieldType || "")
      .trim()
      .toLowerCase(),
  );

const AssignedFormResponse: React.FC = () => {
  const navigate = useNavigate();

  const { task_id } = useParams<{
    task_id: string;
  }>();

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}",
      );
    } catch {
      return {};
    }
  }, []);

  const userId = Number(
    currentUser?.user_id || 0,
  );

  const taskId = Number(task_id || 0);

  const [form, setForm] =
    useState<FormDetails | null>(null);

  const [responses, setResponses] =
    useState<SubmittedResponse[]>([]);

  const [
    selectedResponseId,
    setSelectedResponseId,
  ] = useState<number | null>(null);

  const [loading, setLoading] =
    useState(true);

  const [errorMessage, setErrorMessage] =
    useState("");

  const selectedResponse = useMemo(() => {
    if (!responses.length) {
      return null;
    }

    return (
      responses.find(
        (response) =>
          response.response_id ===
          selectedResponseId,
      ) || responses[0]
    );
  }, [responses, selectedResponseId]);

  const loadResponsePage =
    useCallback(async () => {
      if (!userId) {
        navigate("/login", {
          replace: true,
        });
        return;
      }

      if (
        !Number.isInteger(taskId) ||
        taskId <= 0
      ) {
        setErrorMessage(
          "Invalid form reference.",
        );
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        const [
          formResponse,
          userResponse,
        ] = await Promise.all([
          getTaskDetailsApi(taskId),
          getTaskResponseApi(taskId, userId),
        ]);

        if (!formResponse?.success) {
          throw new Error(
            formResponse?.message ||
              "Unable to load form details.",
          );
        }

        if (!userResponse?.success) {
          throw new Error(
            userResponse?.message ||
              "Unable to load submitted response.",
          );
        }

        const formData =
          formResponse?.data || null;

        const responseData = Array.isArray(
          userResponse?.data,
        )
          ? userResponse.data
          : [];

        const sortedResponses = [
          ...responseData,
        ].sort((a, b) => {
          const first = new Date(
            a.submitted_at || 0,
          ).getTime();

          const second = new Date(
            b.submitted_at || 0,
          ).getTime();

          return second - first;
        });

        setForm(formData);
        setResponses(sortedResponses);

        setSelectedResponseId(
          sortedResponses?.[0]
            ?.response_id || null,
        );
      } catch (error: any) {
        console.error(
          "Assigned form response error:",
          error,
        );

        setForm(null);
        setResponses([]);

        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load the submitted response.",
        );
      } finally {
        setLoading(false);
      }
    }, [navigate, taskId, userId]);

  useEffect(() => {
    loadResponsePage();
  }, [loadResponsePage]);

  const renderAnswerValue = (
    answer: SubmittedAnswer,
  ) => {
    const value = answer.answer;

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return (
        <span className="assigned-response-empty-value">
          No answer provided
        </span>
      );
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return (
          <span className="assigned-response-empty-value">
            No answer provided
          </span>
        );
      }

      return (
        <div className="assigned-response-tags">
          {value.map((item, index) => (
            <span
              key={`${item}-${index}`}
            >
              {item}
            </span>
          ))}
        </div>
      );
    }

    if (isFileType(answer.field_type)) {
      const fileUrl = String(value);

      return (
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="assigned-response-file-link"
        >
          <span className="assigned-response-file-icon">
            <i
              className={
                answer.field_type === "image"
                  ? "feather-image"
                  : "feather-file"
              }
            />
          </span>

          <span>
            <strong>
              {answer.field_type === "image"
                ? "View uploaded image"
                : "View uploaded file"}
            </strong>

            <small>
              Opens in a new tab
            </small>
          </span>

          <i className="feather-external-link" />
        </a>
      );
    }

    if (
      String(answer.field_type)
        .toLowerCase() === "date"
    ) {
      return formatDate(value);
    }

    return String(value);
  };

  const responseStatus =
    String(
      selectedResponse?.status ||
        "submitted",
    )
      .trim()
      .toLowerCase();

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat />

      <main className="main-content right-chat-active assigned-response-main">
        <div className="middle-sidebar-bottom assigned-response-middle">
          <div className="middle-sidebar-left assigned-response-left">
            <div className="assigned-response-wrapper">
              <button
                type="button"
                className="assigned-response-back"
                onClick={() =>
                  navigate("/forms")
                }
              >
                <i className="feather-arrow-left" />
                Back to Assigned Forms
              </button>

              {loading ? (
                <section className="assigned-response-state">
                  <div className="spinner-border text-primary" />
                  <p>
                    Loading your response...
                  </p>
                </section>
              ) : errorMessage ? (
                <section className="assigned-response-state">
                  <div className="assigned-response-state-icon error">
                    <i className="feather-alert-circle" />
                  </div>

                  <h2>
                    Unable to load response
                  </h2>

                  <p>{errorMessage}</p>

                  <button
                    type="button"
                    onClick={loadResponsePage}
                  >
                    Try Again
                  </button>
                </section>
              ) : !form ? (
                <section className="assigned-response-state">
                  <div className="assigned-response-state-icon error">
                    <i className="feather-file-minus" />
                  </div>

                  <h2>
                    Form not found
                  </h2>

                  <p>
                    The requested form is no
                    longer available.
                  </p>
                </section>
              ) : !selectedResponse ? (
                <section className="assigned-response-state">
                  <div className="assigned-response-state-icon empty">
                    <i className="feather-file-text" />
                  </div>

                  <h2>
                    No submitted response
                  </h2>

                  <p>
                    You have not submitted a
                    response for this form yet.
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/forms/${taskId}`,
                      )
                    }
                  >
                    Open Form
                  </button>
                </section>
              ) : (
                <>
                  <section className="assigned-response-header">
                    <div className="assigned-response-header-copy">
                      <div className="assigned-response-title-row">
                        <span className="assigned-response-badge">
                          Submitted Response
                        </span>

                        <span
                          className={`assigned-response-status ${responseStatus}`}
                        >
                          <i className="feather-check-circle" />

                          {responseStatus ===
                          "partially_completed"
                            ? "In Progress"
                            : "Submitted"}
                        </span>
                      </div>

                      <h1>
                        {form.task_title}
                      </h1>

                      <p>
                        {form.task_description ||
                          "Review the answers submitted for this form."}
                      </p>
                    </div>

                    <div className="assigned-response-summary">
                      <div className="assigned-response-summary-icon">
                        <i className="feather-calendar" />
                      </div>

                      <div>
                        <label>
                          Submitted On
                        </label>

                        <strong>
                          {formatDateTime(
                            selectedResponse.submitted_at,
                          )}
                        </strong>
                      </div>
                    </div>
                  </section>

                  {responses.length > 1 && (
                    <section className="assigned-response-switcher">
                      <div>
                        <span>
                          Your Responses
                        </span>

                        <strong>
                          {responses.length}
                        </strong>
                      </div>

                      <div className="assigned-response-select-wrap">
                        <select
                          value={
                            selectedResponseId ||
                            ""
                          }
                          onChange={(event) =>
                            setSelectedResponseId(
                              Number(
                                event.target
                                  .value,
                              ),
                            )
                          }
                        >
                          {responses.map(
                            (
                              response,
                              index,
                            ) => (
                              <option
                                key={
                                  response.response_id
                                }
                                value={
                                  response.response_id
                                }
                              >
                                Response{" "}
                                {responses.length -
                                  index}{" "}
                                -{" "}
                                {formatDateTime(
                                  response.submitted_at,
                                )}
                              </option>
                            ),
                          )}
                        </select>

                        <i className="feather-chevron-down" />
                      </div>
                    </section>
                  )}

                  <section className="assigned-response-content">
                    <div className="assigned-response-content-header">
                      <div>
                        <span>
                          Response Details
                        </span>

                        <h2>
                          Your submitted answers
                        </h2>

                        <p>
                          This response is
                          read-only and linked to
                          your member account.
                        </p>
                      </div>

                      <div className="assigned-response-count">
                        <strong>
                          {
                            selectedResponse
                              .answers.length
                          }
                        </strong>

                        <span>
                          {selectedResponse
                            .answers.length === 1
                            ? "Answer"
                            : "Answers"}
                        </span>
                      </div>
                    </div>

                    <div className="assigned-response-answer-list">
                      {selectedResponse.answers.map(
                        (answer, index) => (
                          <article
                            key={`${answer.field_id}-${index}`}
                            className="assigned-response-answer-card"
                          >
                            <div className="assigned-response-answer-number">
                              {index + 1}
                            </div>

                            <div className="assigned-response-answer-content">
                              <label>
                                {
                                  answer.field_label
                                }
                              </label>

                              <div className="assigned-response-answer-value">
                                {renderAnswerValue(
                                  answer,
                                )}
                              </div>
                            </div>
                          </article>
                        ),
                      )}
                    </div>

                    <div className="assigned-response-actions">
                      <div className="assigned-response-security">
                        <i className="feather-lock" />

                        <span>
                          This response has been
                          submitted securely.
                        </span>
                      </div>

                      <button
                        type="button"
                        className="assigned-response-return"
                        onClick={() =>
                          navigate("/forms")
                        }
                      >
                        Back to Forms
                        <i className="feather-arrow-left" />
                      </button>
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Popupchat />
      <Appfooter />

      <style>{`
        .assigned-response-main {
          min-height: 100vh;
          padding-top: 84px;
          padding-left: 300px;
          padding-right: 330px;
          background: #f5f7fb;
          overflow-x: hidden;
        }

        .assigned-response-middle,
        .assigned-response-left {
          width: 100%;
          max-width: 100%;
        }

        .assigned-response-left {
          padding: 0 12px;
        }

        .assigned-response-wrapper {
          width: 100%;
          max-width: 980px;
          margin: 0 auto;
          padding-bottom: 34px;
        }

        .assigned-response-back {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding: 0;
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
          transition: 0.2s ease;
        }

        .assigned-response-back:hover {
          color: #2563eb;
          transform: translateX(-2px);
        }

        .assigned-response-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          padding: 20px 22px;
          border: 1px solid #e9eef7;
          border-radius: 20px;
          background: linear-gradient(
            135deg,
            #ffffff 0%,
            #f8fbff 100%
          );
          box-shadow: 0 7px 22px rgba(15, 23, 42, 0.035);
        }

        .assigned-response-header-copy {
          min-width: 0;
          flex: 1;
        }

        .assigned-response-title-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 9px;
        }

        .assigned-response-badge,
        .assigned-response-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 11px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
        }

        .assigned-response-badge {
          background: #eef4ff;
          color: #2563eb;
        }

        .assigned-response-status.submitted {
          border: 1px solid #a7f3d0;
          background: #ecfdf5;
          color: #047857;
        }

        .assigned-response-status.partially_completed {
          border: 1px solid #bfdbfe;
          background: #eff6ff;
          color: #1d4ed8;
        }

        .assigned-response-header h1 {
          margin: 0;
          color: #111827;
          font-size: 27px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: -0.3px;
        }

        .assigned-response-header p {
          max-width: 650px;
          margin: 7px 0 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
        }

        .assigned-response-summary {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 220px;
          padding: 11px 13px;
          border: 1px solid #e8eef8;
          border-radius: 15px;
          background: #ffffff;
        }

        .assigned-response-summary-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          flex-shrink: 0;
          border-radius: 12px;
          background: #ecfdf5;
          color: #059669;
        }

        .assigned-response-summary label {
          display: block;
          margin-bottom: 3px;
          color: #94a3b8;
          font-size: 10px;
        }

        .assigned-response-summary strong {
          display: block;
          color: #334155;
          font-size: 11px;
          font-weight: 650;
          white-space: nowrap;
        }

        .assigned-response-switcher {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 14px;
          padding: 13px 15px;
          border: 1px solid #e9eef7;
          border-radius: 16px;
          background: #ffffff;
        }

        .assigned-response-switcher > div:first-child {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 12px;
        }

        .assigned-response-switcher strong {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 26px;
          height: 26px;
          padding: 0 8px;
          border-radius: 999px;
          background: #eef4ff;
          color: #2563eb;
          font-size: 11px;
        }

        .assigned-response-select-wrap {
          position: relative;
          width: min(330px, 100%);
        }

        .assigned-response-select-wrap select {
          width: 100%;
          height: 40px;
          padding: 0 38px 0 13px;
          appearance: none;
          border: 1px solid #dbe7fb;
          border-radius: 11px;
          outline: none;
          background: #ffffff;
          color: #334155;
          font-size: 12px;
        }

        .assigned-response-select-wrap > i {
          position: absolute;
          top: 50%;
          right: 13px;
          transform: translateY(-50%);
          color: #64748b;
          pointer-events: none;
        }

        .assigned-response-content {
          margin-top: 14px;
          padding: 20px 22px;
          border: 1px solid #e9eef7;
          border-radius: 20px;
          background: #ffffff;
          box-shadow: 0 7px 22px rgba(15, 23, 42, 0.035);
        }

        .assigned-response-content-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          padding-bottom: 17px;
          border-bottom: 1px solid #eef2f7;
        }

        .assigned-response-content-header > div:first-child > span {
          display: block;
          margin-bottom: 5px;
          color: #2563eb;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .assigned-response-content-header h2 {
          margin: 0;
          color: #111827;
          font-size: 21px;
          font-weight: 700;
        }

        .assigned-response-content-header p {
          margin: 5px 0 0;
          color: #64748b;
          font-size: 12px;
        }

        .assigned-response-count {
          min-width: 76px;
          padding: 9px 11px;
          border: 1px solid #edf2f7;
          border-radius: 13px;
          background: #f8fafc;
          text-align: center;
        }

        .assigned-response-count strong {
          display: block;
          color: #111827;
          font-size: 17px;
          line-height: 1;
        }

        .assigned-response-count span {
          display: block;
          margin-top: 4px;
          color: #64748b;
          font-size: 10px;
        }

        .assigned-response-answer-list {
          display: flex;
          flex-direction: column;
          gap: 13px;
          margin-top: 17px;
        }

        .assigned-response-answer-card {
          display: grid;
          grid-template-columns: 36px minmax(0, 1fr);
          gap: 12px;
          padding: 15px;
          border: 1px solid #e8eef8;
          border-radius: 16px;
          background: #fbfcff;
        }

        .assigned-response-answer-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: #eef4ff;
          color: #2563eb;
          font-size: 12px;
          font-weight: 700;
        }

        .assigned-response-answer-content {
          min-width: 0;
        }

        .assigned-response-answer-content > label {
          display: block;
          margin-bottom: 9px;
          color: #334155;
          font-size: 13px;
          font-weight: 650;
        }

        .assigned-response-answer-value {
          min-height: 44px;
          padding: 12px 14px;
          border: 1px solid #e8eef8;
          border-radius: 12px;
          background: #ffffff;
          color: #334155;
          font-size: 13px;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }

        .assigned-response-empty-value {
          color: #94a3b8;
          font-style: italic;
        }

        .assigned-response-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .assigned-response-tags span {
          display: inline-flex;
          padding: 5px 9px;
          border-radius: 999px;
          background: #eef4ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 600;
        }

        .assigned-response-file-link {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #2563eb;
          text-decoration: none;
        }

        .assigned-response-file-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border-radius: 10px;
          background: #eef4ff;
        }

        .assigned-response-file-link > span:nth-child(2) {
          min-width: 0;
          flex: 1;
        }

        .assigned-response-file-link strong,
        .assigned-response-file-link small {
          display: block;
        }

        .assigned-response-file-link strong {
          color: #2563eb;
          font-size: 12px;
        }

        .assigned-response-file-link small {
          margin-top: 2px;
          color: #94a3b8;
          font-size: 10px;
        }

        .assigned-response-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-top: 18px;
          padding-top: 17px;
          border-top: 1px solid #eef2f7;
        }

        .assigned-response-security {
          display: flex;
          align-items: center;
          gap: 7px;
          color: #94a3b8;
          font-size: 11px;
        }

        .assigned-response-return,
        .assigned-response-state button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          height: 42px;
          padding: 0 18px;
          border: none;
          border-radius: 13px;
          background: #2563eb;
          color: #ffffff;
          font-size: 12px;
          font-weight: 650;
          box-shadow: 0 8px 16px rgba(37, 99, 235, 0.15);
          transition: 0.2s ease;
        }

        .assigned-response-return:hover,
        .assigned-response-state button:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
        }

        .assigned-response-state {
          padding: 54px 20px;
          border: 1px solid #e9eef7;
          border-radius: 20px;
          background: #ffffff;
          text-align: center;
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.035);
        }

        .assigned-response-state h2 {
          margin: 13px 0 0;
          color: #111827;
          font-size: 21px;
        }

        .assigned-response-state p {
          max-width: 470px;
          margin: 9px auto 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
        }

        .assigned-response-state button {
          margin-top: 18px;
        }

        .assigned-response-state-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 62px;
          height: 62px;
          margin: 0 auto;
          border-radius: 18px;
          font-size: 26px;
        }

        .assigned-response-state-icon.error {
          background: #fef2f2;
          color: #dc2626;
        }

        .assigned-response-state-icon.empty {
          background: #eef4ff;
          color: #2563eb;
        }

        @media (max-width: 1200px) {
          .assigned-response-main {
            padding-top: 78px;
            padding-right: 18px;
            padding-left: 24px;
          }

          .assigned-response-left {
            padding: 0;
          }

          .assigned-response-wrapper {
            max-width: 100%;
          }
        }

        @media (max-width: 991px) {
          .assigned-response-main {
            padding-top: 72px;
            padding-right: 16px;
            padding-left: 16px;
          }

          .assigned-response-header,
          .assigned-response-switcher {
            flex-direction: column;
            align-items: stretch;
          }

          .assigned-response-summary,
          .assigned-response-select-wrap {
            width: 100%;
          }
        }

        @media (max-width: 575px) {
          .assigned-response-main {
            padding-top: 16px;
            padding-right: 12px;
            padding-left: 12px;
          }

          .assigned-response-header,
          .assigned-response-content {
            padding: 15px;
            border-radius: 17px;
          }

          .assigned-response-header h1 {
            font-size: 23px;
          }

          .assigned-response-content-header,
          .assigned-response-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .assigned-response-count {
            width: 100%;
          }

          .assigned-response-answer-card {
            grid-template-columns: 1fr;
          }

          .assigned-response-answer-number {
            width: 30px;
            height: 30px;
          }

          .assigned-response-security {
            justify-content: center;
            text-align: center;
          }

          .assigned-response-return {
            width: 100%;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default AssignedFormResponse;
